"use client";
import React, { useEffect, useRef } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

interface VideoPlayerProps {
    videoSource?: string;
    title?: string;
    description?: string;
    btnLabel?: string;
    btnLink?: string;
}

interface HeroVideo2Props {
    data?: {
        title?: string;
        subtitle?: string;
        btnLink?: string;
        btnText?: string;
        videos?: VideoPlayerProps[];
        isLive?: boolean;
    };
}

export default function HeroVideo2_Layout2({ data }: HeroVideo2Props) {
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

    const bannerVideoData = data?.videos?.[0] || null;
    const videoSources = data?.videos || [];

    useEffect(() => {
        videoRefs.current.forEach((video) => {
            if (video) {
                video.muted = true;
                video.play().catch((e) => console.warn("Autoplay prevented:", e));
            }
        });
    }, []);

    return (
        <section className="relative w-full min-h-screen bg-[var(--bg-col-2)] overflow-hidden">
            {/* Background texture */}
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1603400521630-9f2de124b33b?auto=format&fit=crop&w=2897&q=80')] bg-cover bg-center opacity-10"></div>

            {/* Gradient accents */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[var(--decorative-5)] to-transparent"></div>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[var(--decorative-5)] to-transparent"></div>

            {/* Decorative circles */}
            <div className="absolute top-1/4 -left-24 w-64 h-64 border border-[var(--decorative-5)]/20 rounded-full"></div>
            <div className="absolute bottom-1/3 -right-24 w-80 h-80 border border-[var(--decorative-5)]/15 rounded-full"></div>

            <div className="container mx-auto px-6 py-16 relative z-20">
                {/* Header */}
                {data?.title && (
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        viewport={{ once: true }}
                        className="text-center mb-16 pt-12"
                    >
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-[var(--text-inverse)] mb-8 leading-tight tracking-wide">
                            {data.title.split(",").map((part, index) => (
                                <span
                                    key={index}
                                    className={`block ${index === 0
                                        ? "  italic text-[var(--decorative-5)] font-normal"
                                        : "mt-2 font-light"
                                        }`}
                                >
                                    {part.trim()}
                                </span>
                            ))}
                        </h1>

                        {data?.subtitle && (
                            <p className="text-xl text-[var(--text-inverse)]/80 max-w-3xl mx-auto leading-relaxed font-light tracking-wider">
                                {data.subtitle}
                            </p>
                        )}
                    </motion.div>
                )}

                {/* Hero video */}
                <div className="relative w-full max-w-7xl mx-auto mb-20">
                    {bannerVideoData?.videoSource && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.98 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="relative aspect-[21/9] w-full overflow-hidden shadow-2xl mb-10 border border-[var(--border-light)]"
                        >
                            <video
                                className="w-full h-full object-cover"
                                autoPlay
                                loop
                                muted
                                playsInline
                            >
                                <source
                                    src={bannerVideoData.videoSource}
                                    type="video/mp4"
                                />
                            </video>

                            {/* Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-col-2)]/90 via-transparent to-transparent"></div>

                            {/* Caption */}
                            {(bannerVideoData.title || bannerVideoData.description) && (
                                <div className="absolute bottom-12 left-12 max-w-md">
                                    {bannerVideoData.title && (
                                        <h3 className="  italic text-3xl text-[var(--decorative-5)] mb-3 tracking-wide">
                                            {bannerVideoData.title}
                                        </h3>
                                    )}
                                    {bannerVideoData.description && (
                                        <p className="text-[var(--text-inverse)]/90 font-light tracking-wider">
                                            {bannerVideoData.description}
                                        </p>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Secondary video collage */}
                    {videoSources.length > 1 && (
                        <div className="grid grid-cols-12 grid-rows-2 gap-5 w-full">
                            {videoSources.slice(1, 4).map((video, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.15, duration: 0.6 }}
                                    viewport={{ once: true }}
                                    className={`relative group overflow-hidden border border-[var(--border-light)] ${index === 0
                                        ? "col-span-7 row-span-2 h-[500px]"
                                        : "col-span-5 h-[240px]"
                                        }`}
                                >
                                    {video?.videoSource && (
                                        <>
                                            <video
                                                className="w-full h-full object-cover"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                ref={(el) => (videoRefs.current[index + 1] = el)}
                                            >
                                                <source src={video.videoSource} type="video/mp4" />
                                            </video>

                                            <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-col-2)]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                        </>
                                    )}

                                    {(video?.title || video?.description) && (
                                        <div className="absolute bottom-6 left-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                            {video.title && (
                                                <h3 className="  italic text-xl text-[var(--decorative-5)] mb-1">
                                                    {video.title}
                                                </h3>
                                            )}
                                            {video.description && (
                                                <p className="text-[var(--text-inverse)]/80 text-sm font-light">
                                                    {video.description}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>

                {/* CTA */}
                {data?.btnLink && data?.btnText && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mt-20"
                    >
                        <Link href={data.btnLink} passHref>
                            <button className="px-12 py-5 bg-transparent border border-[var(--decorative-5)] text-[var(--decorative-5)] font-light tracking-widest rounded-none hover:bg-[var(--decorative-5)] hover:text-[var(--text-2)] transition-all duration-500 text-lg uppercase">
                                {data.btnText}
                                <svg
                                    className="w-5 h-5 ml-3 inline"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="1.5"
                                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                                    />
                                </svg>
                            </button>
                        </Link>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
